import { Button, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import CheckboxValueGroup from '../../components/CheckboxValueGroup/CheckboxValueGroup';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import { GridRendererProps } from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import { useStore } from '../../hooks/use-store';
import { User } from '../../types/practice-types';

const PHONE_MAX_LENGTH = 10;

interface RowCellRendererProps extends GridRendererProps<User> {
  field: keyof User;
}

const AddUsersRowCellRenderer = observer((props: RowCellRendererProps) => {
  const { row, index, field } = props;
  const store = useStore().addUsersStore;
  const { loaders } = store;
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (field === 'firstName' || index === 0) {
      ref.current?.focus();
    }
  }, [field, index]);

  const changeValue = (fieldToChange: keyof User, value: string): void => {
    if (fieldToChange === 'roles' || (fieldToChange === 'phone' && !/^\d*$/.test(value))) return;
    runInAction(() => {
      row[fieldToChange] = value;
      store.validated = false;
      store.userCreateObjects[index].errors[fieldToChange] = undefined;
    });
  };

  if (field !== 'roles') {
    return (
      <FieldGroupInCell
        htmlFor={`table-cell-text-box_${field}`}
        errorText={store.userCreateObjects[index].errors[field]}
      >
        <TextBox
          ref={ref}
          id={`table-cell-text-box_${field}`}
          data-testid={`table-cell-text-box_${field}`}
          placeholder={field === 'phone' ? 'Add Phone' : field === 'email' ? 'MyIDEXX email' : ''}
          value={row[field] || ''}
          maxLength={field === 'phone' ? PHONE_MAX_LENGTH : undefined}
          onChange={e => changeValue(field, e.target.value)}
          disabled={loaders.validateUsers}
        />
      </FieldGroupInCell>
    );
  }
  if (field === 'roles') {
    return (
      <CheckboxValueGroup
        id={`practice-roles-group_${index}`}
        elementsClass='item-editors'
        variant='horizontal'
        small
        values={store.roles}
        displayField='description'
        onChange={indices => store.changeRoleIndices(index, indices)}
        checkedIndices={store.userCreateObjects[index].roleIndices}
        allDisabled={loaders.validateUsers}
        data-testid='add-user-role'
      />
    );
  }
  return null;
});

export const AddUsersActionsRenderer = observer((props: GridRendererProps<User>) => {
  const { row } = props;
  const store = useStore().addUsersStore;
  return (
    <Button
      variant='link'
      iconOnly='delete'
      onClick={() => store.deleteAddUsersRow(row)}
      title='Delete user'
      aria-label='Delete Added User'
      data-testid={`delete-add-user-${row.email}-btn`}
    />
  );
});

export const ModalCustomFooter = observer(() => {
  const store = useStore().addUsersStore;
  const { userCreateObjects, loaders, usersValid } = store;

  return (
    <>
      <Button
        variant='secondary'
        onClick={() => store.addUsersRow()}
        disabled={loaders.createUsers || loaders.validateUsers}
      >
        Add Row
      </Button>
      <Button
        variant='secondary'
        disabled={userCreateObjects.length === 0 || loaders.createUsers}
        onClick={() => void store.validateAddUsers()}
      >
        <Loader loading={loaders.validateUsers}>Validate</Loader>
      </Button>
      <Button
        variant='primary'
        disabled={!usersValid || loaders.validateUsers}
        aria-label='Create Users'
        onClick={() => void store.createUsers()}
      >
        <Loader loading={loaders.createUsers}>Validate</Loader>
        Create User(s)
      </Button>
    </>
  );
});

export const addUsersRowCellRenderer =
  (field: keyof User) =>
  (props: GridRendererProps<User>): React.JSX.Element => (
    <AddUsersRowCellRenderer row={props.row} index={props.index} field={field} />
  );
